import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const AppContext = createContext(null);

const readLS = (k, def) => {
  try {
    const v = localStorage.getItem(k);
    return v ? JSON.parse(v) : def;
  } catch {
    return def;
  }
};
const writeLS = (k, v) => {
  try {
    localStorage.setItem(k, JSON.stringify(v));
  } catch {}
};

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [cart, setCart] = useState(() => readLS("tf_cart", []));
  const [wishlist, setWishlist] = useState(() => readLS("tf_wishlist", []));
  const [theme, setTheme] = useState(() => localStorage.getItem("tf_theme") || "light");
  const syncingRef = useRef(false); // suppress push-to-server while we're pulling from server

  // Theme apply
  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("tf_theme", theme);
  }, [theme]);
  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  // Persist locally so guests never lose their cart / wishlist
  useEffect(() => {
    writeLS("tf_cart", cart);
  }, [cart]);
  useEffect(() => {
    writeLS("tf_wishlist", wishlist);
  }, [wishlist]);

  // ---------- Auth ----------
  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes("session_id=")) {
      setAuthLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/customer/me");
      setUser(data);
    } catch {
      // fall back to legacy /auth/me for admin session cookies
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        setUser(null);
      }
    } finally {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // ---------- Sync cart & wishlist with server when logged in ----------
  const pullServerState = useCallback(async () => {
    try {
      syncingRef.current = true;
      const [cartRes, wishRes] = await Promise.all([
        api.get("/customer/cart").catch(() => null),
        api.get("/customer/wishlist").catch(() => null),
      ]);
      if (cartRes?.data) setCart(cartRes.data.items || []);
      if (wishRes?.data) setWishlist(wishRes.data.products || []);
    } finally {
      // Yield a tick before re-enabling push-to-server so state updates settle first
      setTimeout(() => {
        syncingRef.current = false;
      }, 300);
    }
  }, []);

  // On login, merge local cart/wishlist into server then pull merged state
  useEffect(() => {
    if (!user || user.is_admin) return;
    (async () => {
      try {
        const localCart = readLS("tf_cart", []);
        if (localCart.length) {
          await api.post("/customer/cart/merge", {
            items: localCart.map((i) => ({ product_id: i.product_id, size: i.size, qty: i.qty })),
          });
        }
        const localWish = readLS("tf_wishlist", []);
        for (const p of localWish) {
          try {
            await api.post("/customer/wishlist/add", { product_id: p.id });
          } catch {}
        }
      } catch {}
      await pullServerState();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  // Push cart changes to server (debounced)
  useEffect(() => {
    if (!user || user.is_admin || syncingRef.current) return;
    const t = setTimeout(() => {
      api
        .put("/customer/cart", {
          items: cart.map((i) => ({ product_id: i.product_id, size: i.size, qty: i.qty })),
        })
        .catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [cart, user]);

  const login = async (identifier, password) => {
    const { data } = await api.post("/auth/login", { identifier, password });
    localStorage.setItem("tf_token", data.token);
    setUser(data.user);
    toast.success("Welcome back.");
    return data.user;
  };
  const signup = async ({ name, email, password, phone }) => {
    const { data } = await api.post("/customer/signup", { name, email, password, phone });
    localStorage.setItem("tf_token", data.token);
    setUser(data.user);
    return data.user;
  };
  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    localStorage.removeItem("tf_token");
    setUser(null);
    setCart([]);
    setWishlist([]);
    toast.success("Signed out.");
  };
  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/customer/me");
      setUser(data);
    } catch {}
  }, []);

  // ---------- Cart ----------
  const addToCart = (product, size, qty = 1) => {
    setCart((prev) => {
      const idx = prev.findIndex((i) => i.product_id === product.id && i.size === size);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: next[idx].qty + qty };
        return next;
      }
      const price = product.discount_price || product.price;
      return [
        ...prev,
        {
          product_id: product.id,
          name: product.name,
          image: product.image,
          price,
          size,
          qty,
        },
      ];
    });
    toast.success(`Added to cart — ${product.name} (${size})`);
  };
  const updateCartQty = (product_id, size, qty) => {
    setCart((prev) =>
      prev.map((i) => (i.product_id === product_id && i.size === size ? { ...i, qty: Math.max(1, qty) } : i))
    );
  };
  const removeFromCart = (product_id, size) => {
    setCart((prev) => prev.filter((i) => !(i.product_id === product_id && i.size === size)));
  };
  const clearCart = () => setCart([]);

  // ---------- Wishlist ----------
  const toggleWishlist = (product) => {
    const exists = wishlist.find((i) => i.id === product.id);
    if (exists) {
      setWishlist((prev) => prev.filter((i) => i.id !== product.id));
      if (user && !user.is_admin) api.post("/customer/wishlist/remove", { product_id: product.id }).catch(() => {});
      toast.success("Removed from wishlist");
    } else {
      setWishlist((prev) => [...prev, product]);
      if (user && !user.is_admin) api.post("/customer/wishlist/add", { product_id: product.id }).catch(() => {});
      toast.success("Saved to wishlist");
    }
  };
  const inWishlist = (id) => wishlist.some((i) => i.id === id);

  return (
    <AppContext.Provider
      value={{
        user,
        setUser,
        authLoading,
        login,
        signup,
        logout,
        checkAuth,
        refreshUser,
        cart,
        addToCart,
        updateCartQty,
        removeFromCart,
        clearCart,
        wishlist,
        toggleWishlist,
        inWishlist,
        theme,
        toggleTheme,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be inside AppProvider");
  return ctx;
};
